import AccessibilityHelper from '../../00_utilities/helpers/AccessibilityHelper';

class Counter {
    constructor(elem) {
        this.elem = elem;
        this.breakpoints = {
            'screen-sm': 768,
        };
        this.counterElements = this.elem.querySelectorAll('[data-count-to]');
        this.counterEndedEvent = 'counter-ended';
        this.animationEndedEvent = 'animation-ended';
    }

    init() {
        const hasOSReducedMotion = AccessibilityHelper.prefersReducedMotion();
        if (!hasOSReducedMotion && this.counterElements) {
            this.initIntersectionObserver();
        }
    }

    initIntersectionObserver() {
        const options = {
            threshold: [0.5, 0.75, 1],
        };
        const observer = new IntersectionObserver((entries, self) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.initializeCounterAnimation();
                    self.unobserve(this.elem);
                }
            });
        }, options);

        observer.observe(this.elem);
    }

    initializeCounterAnimation() {
        this.counterElements.forEach((element, index) => {
            element.innerHTML = '0';

            setTimeout(() => {
                Counter.dispatchCounterEndedEvent(element, this.counterEndedEvent);
                Counter.dispatchCounterEndedEvent(element, this.animationEndedEvent);
            }, index * 800);

            element.addEventListener(this.counterEndedEvent, () => {
                this.triggerNextAnimation(element);
            });

            element.addEventListener(this.animationEndedEvent, (event) => {
                const nextToAnimate = Counter.getNextCounterItem(element);
                if (nextToAnimate && event.detail.nextToAnimate === nextToAnimate) {
                    const numbers = Counter.prepareNumbers(nextToAnimate);
                    const { countTo, numberDecimal, useGroupingFormat } = numbers;
                    Counter.countUp(nextToAnimate, countTo, numberDecimal, useGroupingFormat);
                }
            });
        });

        const numbers = Counter.prepareNumbers(this.counterElements[0]);
        const { element, countTo, numberDecimal, useGroupingFormat } = numbers;
        Counter.countUp(element, countTo, numberDecimal, useGroupingFormat);
    }

    static prepareNumbers(element) {
        if (element) {
            const initialString = element.getAttribute('data-count-to');
            const useGroupingFormat = (element.getAttribute('data-count-grouping-format') === 'true');
            const englishString = initialString.replace(',', '.');
            const numberDecimal = englishString.split('.')[1] ? englishString.split('.')[1].length : 0;

            const countTo = Number(englishString).toFixed(numberDecimal);

            if (!Number.isNaN(Number(englishString))) {
                return { element, countTo, numberDecimal, useGroupingFormat };
            }
        }
        return null;
    }

    static countUp(element, countTo, numberDecimal, useGroupingFormat = true) {
        const pageLanguage = document.documentElement.lang;
        const animationDuration = 3000; // ms
        const frameDuration = 1000 / 60;
        const totalFrames = Math.round(animationDuration / frameDuration);
        // easing functions https://gist.github.com/gre/1650294#file-easing-js
        const easeInOutQuart = (t) => {
            if (t < 0.5) {
                return 8 * t * t * t * t;
            }
            return 1 - 8 * --t * t * t * t; // eslint-disable-line no-plusplus
        };
        let frame = 0;

        const counter = setInterval(() => {
            frame += 1;

            const progress = easeInOutQuart(frame / totalFrames);
            const currentCount = (countTo * progress);

            const localizedCurrentCount = new Intl.NumberFormat(pageLanguage, {
                minimumFractionDigits: numberDecimal,
                maximumFractionDigits: numberDecimal,
                useGrouping: useGroupingFormat,
            }).format(currentCount);

            if (parseInt(element.innerHTML, 10) !== currentCount) {
                element.innerHTML = localizedCurrentCount.toString();
            }

            if (frame === totalFrames) {
                clearInterval(counter);
            }
        }, frameDuration);
    }

    triggerNextAnimation(element) {
        const counterItem = element.closest('.counter__item');
        if (counterItem) {
            counterItem.classList.add('highlighter-active');
        }
    }

    static dispatchCounterEndedEvent(element, event) {
        const nextToAnimate = Counter.getNextCounterItem(element);
        element.dispatchEvent(new CustomEvent(event, {
            detail: { nextToAnimate },
        }));
    }

    static getNextCounterItem(element) {
        const item = element.closest('.counter__item');
        if (item) {
            const { nextSibling } = item;
            if (nextSibling) {
                return nextSibling.querySelector('[data-count-to]');
            }
        }

        return null;
    }
}

export default Counter;
